import React, { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Search, Sparkles } from 'lucide-react';

interface AgentProfile {
  agent_name?: string;
  tagline?: string;
  description?: string;
}

interface SuggestedSearchesProps {
  welcomeMessage?: string | null;
  suggestions: string[];
  onSelect: (query: string) => void;
  displayCount?: number;
  agentAvatarUrl?: string | null;
  agentProfile?: AgentProfile | null;
}

function shuffleAndPick<T>(arr: T[], count: number): T[] {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

const SuggestedSearches: React.FC<SuggestedSearchesProps> = ({
  welcomeMessage,
  suggestions,
  onSelect,
  displayCount = 5,
  agentAvatarUrl,
  agentProfile,
}) => {
  const displayed = useMemo(
    () => shuffleAndPick(suggestions, Math.min(displayCount, suggestions.length)),
    // Randomize once per mount — not on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [suggestions.length, displayCount]
  );

  if (!welcomeMessage && displayed.length === 0) return null;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      padding: '2rem',
      gap: '1.5rem',
      maxWidth: '640px',
      margin: '0 auto',
    }}>
      {/* Agent Avatar & Profile */}
      {(agentAvatarUrl || agentProfile) && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '0.5rem',
        }}>
          {agentAvatarUrl && (
            <img
              src={agentAvatarUrl}
              alt={agentProfile?.agent_name || 'Agent'}
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #8b5cf6',
                boxShadow: '0 0 20px rgba(139, 92, 246, 0.3)',
              }}
            />
          )}
          {agentProfile?.agent_name && (
            <span style={{
              color: '#e0e0e0',
              fontSize: '1.1rem',
              fontWeight: 600,
            }}>
              {agentProfile.agent_name}
            </span>
          )}
          {agentProfile?.tagline && (
            <span style={{
              color: '#9ca3af',
              fontSize: '0.85rem',
              fontStyle: 'italic',
              textAlign: 'center',
              maxWidth: '400px',
            }}>
              {agentProfile.tagline}
            </span>
          )}
        </div>
      )}

      {welcomeMessage && (
        <div style={{
          textAlign: 'center',
          color: '#e0e0e0',
          fontSize: '1rem',
          lineHeight: '1.6',
          marginBottom: '0.5rem',
        }}>
          {!agentAvatarUrl && !agentProfile && (
            <Sparkles size={24} style={{ color: '#8b5cf6', marginBottom: '0.75rem' }} />
          )}
          <div style={{ margin: 0 }}><ReactMarkdown>{welcomeMessage}</ReactMarkdown></div>
        </div>
      )}

      {displayed.length > 0 && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          width: '100%',
        }}>
          <span style={{
            fontSize: '0.75rem',
            color: '#666',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.25rem',
          }}>
            Try asking...
          </span>
          {displayed.map((query, i) => (
            <button
              key={i}
              onClick={() => onSelect(query)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem 1rem',
                background: '#1e1e22',
                border: '1px solid #2e2e2e',
                borderRadius: '10px',
                color: '#ccc',
                fontSize: '0.875rem',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                width: '100%',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#252530';
                e.currentTarget.style.borderColor = '#8b5cf6';
                e.currentTarget.style.color = '#e0e0e0';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = '#1e1e22';
                e.currentTarget.style.borderColor = '#2e2e2e';
                e.currentTarget.style.color = '#ccc';
              }}
            >
              <Search size={14} style={{ flexShrink: 0, color: '#8b5cf6' }} />
              {query}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SuggestedSearches;
